import { normalizeVendor } from "./normalizeVendor";
import type {
  ColumnMapping,
  CsvEntity,
  MappedRow,
  ParsedCsv,
} from "./types";

interface MapResult {
  rows: MappedRow[];
  errors: Array<{ rowIndex: number; reason: string }>;
}

/**
 * Parse a date cell into a normalized YYYY-MM-DD.
 *
 * Accepts:
 *   - YYYY-MM-DD (ISO)
 *   - YYYY/MM/DD
 *   - MM/DD/YYYY (US)
 *   - DD/MM/YYYY (EU) — chosen when MM > 12
 *
 * Returns null when the string cannot be confidently parsed.
 */
function parseDate(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;

  // ISO: YYYY-MM-DD (or with time — strip it).
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/.exec(s);
  if (iso) {
    const [, y, m, d] = iso;
    if (isValidYmd(+y!, +m!, +d!)) return `${y}-${m}-${d}`;
    return null;
  }

  // YYYY/MM/DD
  const ymdSlash = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(s);
  if (ymdSlash) {
    const [, y, m, d] = ymdSlash;
    if (isValidYmd(+y!, +m!, +d!)) {
      return `${y}-${pad2(+m!)}-${pad2(+d!)}`;
    }
    return null;
  }

  // MM/DD/YYYY or DD/MM/YYYY — disambiguate by first segment.
  const slash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (slash) {
    const [, a, b, y] = slash;
    const aNum = +a!;
    const bNum = +b!;
    const yNum = +y!;
    // If the first number is > 12 it cannot be a month — must be DD/MM/YYYY.
    // If the second is > 12 it cannot be a month — must be MM/DD/YYYY.
    // Default to MM/DD/YYYY (US) when ambiguous.
    let month: number;
    let day: number;
    if (aNum > 12 && bNum <= 12) {
      day = aNum;
      month = bNum;
    } else if (bNum > 12 && aNum <= 12) {
      month = aNum;
      day = bNum;
    } else {
      month = aNum;
      day = bNum;
    }
    if (isValidYmd(yNum, month, day)) {
      return `${yNum}-${pad2(month)}-${pad2(day)}`;
    }
    return null;
  }

  return null;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function isValidYmd(y: number, m: number, d: number): boolean {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) {
    return false;
  }
  if (y < 1900 || y > 2999) return false;
  if (m < 1 || m > 12) return false;
  if (d < 1 || d > 31) return false;
  // Round-trip via Date for month-length correctness (no leap-year bugs).
  const probe = new Date(Date.UTC(y, m - 1, d));
  return (
    probe.getUTCFullYear() === y &&
    probe.getUTCMonth() === m - 1 &&
    probe.getUTCDate() === d
  );
}

/**
 * Parse an amount string into integer cents, preserving sign.
 * Accepts `$1,234.56`, `1.234,56` (EU), `(42.00)` (neg), bare `42`.
 */
export function parseAmount(raw: string): number | null {
  const s = raw.trim();
  if (!s) return null;

  let sign = 1;
  let body = s;

  // Accountants' parentheses = negative.
  if (/^\(.*\)$/.test(body)) {
    sign = -1;
    body = body.slice(1, -1).trim();
  }

  // Strip currency symbols and any leading + or explicit unary -.
  body = body.replace(/^[+\-]/, (m) => {
    if (m === "-") sign = -sign;
    return "";
  });
  body = body.replace(/[$€£¥₹]/g, "").trim();
  // Strip trailing currency code like "USD" / "EUR".
  body = body.replace(/\s*[A-Za-z]{3}\s*$/, "").trim();

  if (!body) return null;

  // Figure out decimal separator. European style is `1.234,56` — comma is
  // the decimal, dots are thousands separators. US/UK style is `1,234.56`.
  const hasComma = body.includes(",");
  const hasDot = body.includes(".");

  let normalized: string;
  if (hasComma && hasDot) {
    // Whichever appears last is the decimal separator.
    const lastComma = body.lastIndexOf(",");
    const lastDot = body.lastIndexOf(".");
    if (lastComma > lastDot) {
      // EU: strip dots, replace comma with dot.
      normalized = body.replace(/\./g, "").replace(",", ".");
    } else {
      // US: strip commas.
      normalized = body.replace(/,/g, "");
    }
  } else if (hasComma) {
    // Comma alone could be thousands (1,234) or decimal (1,23). Use the
    // position after the last comma: if there are exactly 2 digits, treat as
    // decimal; otherwise as thousands.
    const after = body.slice(body.lastIndexOf(",") + 1);
    if (/^\d{2}$/.test(after) && body.indexOf(",") === body.lastIndexOf(",")) {
      normalized = body.replace(",", ".");
    } else {
      normalized = body.replace(/,/g, "");
    }
  } else {
    normalized = body;
  }

  normalized = normalized.replace(/\s/g, "");
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;

  const asFloat = parseFloat(normalized);
  if (!Number.isFinite(asFloat)) return null;
  const cents = Math.round(asFloat * 100) * sign;
  return cents;
}

function normalizeDirection(
  raw: string,
): "incoming" | "outgoing" | undefined {
  const s = raw.trim().toLowerCase();
  if (!s) return undefined;
  if (
    s === "incoming" ||
    s === "in" ||
    s === "credit" ||
    s === "cr" ||
    s === "+" ||
    s === "receivable" ||
    s === "ar"
  ) {
    return "incoming";
  }
  if (
    s === "outgoing" ||
    s === "out" ||
    s === "debit" ||
    s === "dr" ||
    s === "-" ||
    s === "payable" ||
    s === "ap"
  ) {
    return "outgoing";
  }
  return undefined;
}

async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  const arr = new Uint8Array(digest);
  let out = "";
  for (let i = 0; i < arr.length; i++) {
    out += arr[i]!.toString(16).padStart(2, "0");
  }
  return out;
}

export async function hashRow(
  date: string,
  amountCents: number,
  vendorNormalized: string,
): Promise<string> {
  return sha256Hex(`${date}|${amountCents}|${vendorNormalized}`);
}

/**
 * Map every CSV row through the user-chosen column mapping and convert the
 * fields into our internal shape. Rows with unparseable dates or amounts go
 * into `errors[]`; everything else lands in `rows`, each with a dedupe hash.
 */
export async function mapRows(
  parsed: ParsedCsv,
  mapping: ColumnMapping,
  entity: CsvEntity,
  _tzOffsetMinutes?: number,
): Promise<MapResult> {
  const rows: MappedRow[] = [];
  const errors: Array<{ rowIndex: number; reason: string }> = [];

  // Resolve column indices once.
  const dateIdx = mapping.mappings.indexOf("date");
  const counterpartyIdx = mapping.mappings.indexOf("counterparty");
  const amountIdx = mapping.mappings.indexOf("amount");
  const directionIdx = mapping.mappings.indexOf("direction");
  const notesIdx = mapping.mappings.indexOf("notes");

  for (let rowIndex = 0; rowIndex < parsed.rows.length; rowIndex++) {
    const row = parsed.rows[rowIndex]!;

    const rawDate = dateIdx >= 0 ? (row[dateIdx] ?? "") : "";
    const rawCounterparty =
      counterpartyIdx >= 0 ? (row[counterpartyIdx] ?? "") : "";
    const rawAmount = amountIdx >= 0 ? (row[amountIdx] ?? "") : "";
    const rawDirection =
      directionIdx >= 0 ? (row[directionIdx] ?? "") : "";
    const rawNotes = notesIdx >= 0 ? (row[notesIdx] ?? "") : "";

    const date = parseDate(rawDate);
    if (!date) {
      errors.push({
        rowIndex,
        reason: `Invalid date: "${rawDate.trim()}"`,
      });
      continue;
    }

    const amountCents = parseAmount(rawAmount);
    if (amountCents === null) {
      errors.push({
        rowIndex,
        reason: `Unparseable amount: "${rawAmount.trim()}"`,
      });
      continue;
    }

    const counterparty = rawCounterparty.trim();
    if (!counterparty) {
      errors.push({
        rowIndex,
        reason: "Missing counterparty/vendor",
      });
      continue;
    }

    const vendorNormalized = normalizeVendor(counterparty);
    const hash = await hashRow(date, amountCents, vendorNormalized);

    const mapped: MappedRow = {
      date,
      counterparty,
      amountCents,
      hash,
      rawIndex: rowIndex,
    };

    if (entity === "invoice" && directionIdx >= 0) {
      const dir = normalizeDirection(rawDirection);
      if (dir) mapped.direction = dir;
    }

    if (notesIdx >= 0 && rawNotes.trim()) {
      mapped.notes = rawNotes.trim();
    }

    rows.push(mapped);
  }

  return { rows, errors };
}
